import React, { useEffect } from 'react';
import { Calendar, Heart, ArrowRight, Share2, Facebook, Twitter, Mail, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import NavBar from '@/components/NavBar';
// Removed Footer import - App.tsx handles global footer
import { BOOKING_URL } from '@/constants/urls';
import { WelcomeModal } from '@/components/WelcomeModal';
import BlogPostingSchema from '@/components/BlogPostingSchema';
import preconceptionImage from '@/assets/blog/3month-preconception.webp';

const PreconceptionWindowBlog = () => {
  // Force scroll to top on page load (ignoring anchors)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = "The Preconception Window | Women and the Way";
    const text = "Why the 3 months before conception play a crucial role in fertility outcomes. Learn how to prepare your body for conception and pregnancy.";
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'email':
        const subject = "The Preconception Window";
        const body = `${text}\n\nRead more: ${url}`;
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
        break;
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Add dynamic meta tags and structured data
  useEffect(() => {
    // Update document title and meta tags
    document.title = "Preconception Window Currimundi | 3-Month Fertility Prep";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Learn about the critical 3-month preconception window at our Currimundi Chinese Medicine clinic. Prepare your body for conception and pregnancy with expert fertility guidance.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Preconception Window Currimundi | 3-Month Fertility Prep");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Learn about the critical 3-month preconception window at our Currimundi Chinese Medicine clinic. Prepare your body for conception and pregnancy with expert fertility guidance.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/blog/preconception-window");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', preconceptionImage);
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Preconception Window Currimundi | 3-Month Fertility Prep");
    }

    // Cleanup function
    return () => {
      // Reset title on unmount
      document.title = "Acupuncture Sunshine Coast & Caloundra | Women and The Way - Chinese Medicine";
    };
  }, []);

  return (
    <>
      <BlogPostingSchema
        title="The Critical 3-Month Preconception Window"
        description="Why the 3 months before conception play a crucial role in fertility outcomes. Learn how to prepare your body for conception and pregnancy."
        image={preconceptionImage}
        url="https://womenandtheway.com.au/blog/preconception-window"
        publishedDate="2025-01-15"
        modifiedDate="2025-01-27"
        keywords="preconception window, fertility preparation, pregnancy planning, women's health, Sunshine Coast, fertility clinic, Chinese medicine"
        articleSection="Fertility & Women's Health"
      />
      <NavBar />
      <div className="min-h-screen bg-warm-beige pt-24 md:pt-32">
        {/* Hero Section with Image */}
        <section className="relative py-16 px-6 bg-gradient-to-r from-sage-50 via-white to-amber-50">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col md:flex-row items-center md:justify-between">
              <div className="w-full md:w-1/2 mb-10 md:mb-0">
                <div className="mb-8">
                  <span className="text-5xl md:text-6xl mb-4 block animate-float">🕰️</span>
                  <h1 className="font-serif text-4xl md:text-5xl font-bold mb-6 leading-tight bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                    The Preconception Window
                  </h1>
                  <p className="text-xl text-gray-600 font-light">
                    Why Timing Matters for Fertility
                  </p>
                </div>
              </div>
              <div className="w-full md:w-5/12 relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-amber-200/50">
                  <img 
                    src={preconceptionImage} 
                    alt="The critical 3-month preconception window at Women and the Way, Sunshine Coast – understanding fertility timing and preparation" 
                    className="w-full h-auto object-cover aspect-[4/3]"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-lg border border-amber-200/50 w-3/4 md:w-1/2">
                  <p className="text-sage-800 font-serif font-medium">
                    "The best time to plant a tree was 20 years ago. The second best time is now."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="py-12 px-6">
          <div className="container mx-auto max-w-3xl">
            {/* Introduction */}
            <div className="prose prose-lg max-w-none mb-16">
              <p className="text-lg leading-relaxed text-gray-700 mb-6">
                The months before conception are a unique window of opportunity. At Women and the Way, we guide you to make the most of this time - nourishing your body, mind, and spirit for a healthy pregnancy and baby.
              </p>
              <p className="text-lg leading-relaxed text-gray-700 mb-6">
                Science shows that the preconception period (at least 3-4 months before trying to conceive) is when you can have the greatest impact on egg and sperm quality, hormone balance, and overall fertility.
              </p>
              <p className="text-lg leading-relaxed text-gray-700">
                Let's explore why this window matters and how you can make the most of it.
              </p>
            </div>

            {/* Why Preconception Matters */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🌱</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Why Preconception Matters</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    The health of your eggs and sperm is shaped by your environment, nutrition, stress, and lifestyle in the months before conception. This is when you can:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Boost fertility naturally</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Reduce risk of miscarriage</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Support healthy fetal development</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </section>

            {/* What to Focus On */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🧘‍♀️</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">What to Focus On</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="bg-soft-pink border-2 border-amber-200/50 shadow-sm rounded-2xl">
                  <CardContent className="p-8">
                    <h3 className="font-serif text-2xl font-semibold mb-2 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Nutrition</h3>
                    <p className="text-gray-700 mb-4">Eat a rainbow of whole foods, focus on protein, healthy fats, and leafy greens.</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                  <CardContent className="p-8">
                    <h3 className="font-serif text-2xl font-semibold mb-2 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Lifestyle</h3>
                    <p className="text-gray-700 mb-4">Prioritise sleep, manage stress, and move your body daily.</p>
                  </CardContent>
                </Card>
              </div>
              <div className="mt-8 p-6 bg-sage-green/10 rounded-lg border-2 border-amber-200/50">
                <p className="text-gray-700">
                  <strong>Tip:</strong> Start a preconception journal to track your habits and progress.
                </p>
              </div>
            </section>

            {/* Partner's Role */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🤝</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Your Partner Matters Too</span>
              </h2>
              <Card className="bg-white rounded-2xl shadow-sm border-2 border-amber-200/50 mb-8">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    Sperm health is just as important as egg health. Encourage your partner to join you in making healthy changes - fertility is a team effort!
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Share Component */}
            <div className="my-8 text-center">
              <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-amber-200/50">
                <h3 className="font-serif text-xl font-semibold mb-4 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Share this article</h3>
                <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 border-sage-green text-sage-green hover:bg-sage-green hover:text-white whitespace-nowrap"
                  >
                    <Facebook className="w-4 h-4" />
                    <span>Facebook</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 border-sage-green text-sage-green hover:bg-sage-green hover:text-white whitespace-nowrap"
                  >
                    <Twitter className="w-4 h-4" />
                    <span>Twitter/X</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('email')}
                    className="flex items-center space-x-2 border-sage-green text-sage-green hover:bg-sage-green hover:text-white whitespace-nowrap"
                  >
                    <Mail className="w-4 h-4" />
                    <span>Email</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="flex items-center space-x-2 border-sage-green text-sage-green hover:bg-sage-green hover:text-white whitespace-nowrap"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copy Link</span>
                  </Button>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <section className="text-center py-16">
              <div className="bg-sage-800 rounded-2xl p-12 shadow-lg border-2 border-amber-200/50 max-w-2xl mx-auto">
                <h2 className="font-serif text-3xl font-bold text-white mb-6">Ready to Start Your Preconception Journey?</h2>
                <p className="text-lg text-sage-100 mb-8 max-w-2xl mx-auto">
                  The preconception window is your chance to lay the foundation for a healthy pregnancy and baby. Take the first step today!
                </p>
                <Button 
                  size="lg" 
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-semibold rounded-full"
                  onClick={() => window.open(BOOKING_URL, '_blank')}
                >
                  Book Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-sm text-sage-200 mt-4">Empower your fertility journey</p>
              </div>
            </section>
          </div>
        </main>
      </div>
              {/* Footer removed - App.tsx handles global footer */}
      <WelcomeModal />
    </>
  );
};

export default PreconceptionWindowBlog; 